import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatRand, mediaUrl } from "@/lib/api";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    if (!user) navigate("/login?redirect=/checkout");
    else navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-white w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-6 border-b border-purple-100">
          <SheetTitle className="flex items-center gap-2 font-display text-xl">
            <ShoppingBag className="w-5 h-5 text-fuchsia-500" /> Your Shopping Bag
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-3">
            <div className="w-16 h-16 rounded-full bg-purple-50 flex items-center justify-center">
              <ShoppingBag className="w-7 h-7 text-purple-300" />
            </div>
            <p className="text-muted-foreground">Your bag is empty.</p>
            <button
              onClick={() => {
                setOpen(false);
                navigate("/shop");
              }}
              className="text-fuchsia-600 font-semibold hover:underline"
            >
              Start shopping →
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="cart-items-list">
              {items.map((item) => (
                <div key={item.key} data-testid={`cart-item-${item.product_id}`} className="flex gap-3">
                  <img src={mediaUrl(item.image)} alt={item.name} className="w-20 h-24 rounded-xl object-contain bg-purple-50 p-1" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-sm leading-snug line-clamp-2">{item.name}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5" data-testid={`cart-item-variant-${item.key}`}>
                      {item.colour && <>Colour: {item.colour} · </>}Size: {item.size}
                    </p>
                    <p className="text-sm font-bold brand-gradient-text mt-1">{formatRand(item.price)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center border border-purple-200 rounded-full">
                        <button className="p-1.5" onClick={() => updateQty(item.key, item.quantity - 1)}>
                          <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span className="px-2 text-sm font-medium w-6 text-center">{item.quantity}</span>
                        <button className="p-1.5" onClick={() => updateQty(item.key, item.quantity + 1)}>
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${item.product_id}`}
                        onClick={() => removeItem(item.key)}
                        className="p-1.5 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-purple-100 p-6 space-y-4">
              <div className="flex items-center justify-between text-lg">
                <span className="font-medium">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-bold brand-gradient-text">{formatRand(total)}</span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={goCheckout}
                className="w-full py-3.5 rounded-full brand-gradient text-white font-semibold hover:opacity-90 transition"
              >
                Proceed to Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
